import React, { useEffect, useRef, useState } from 'react'
import browser from 'webextension-polyfill'
import t from '~t'
import { Button, Icon } from '~ui'
import Screen from '~co/screen/basic'
import { DealChat, resolveChatTarget, resolveChatTargetFuzzy } from '~modules/deals'

//Side-panel surface for the LTDHunt discussion thread. Resolution order:
//  1. dealChatTarget stamped by the popup Discuss action — only when its
//     tabId+url match the active tab (a stored target must never reopen
//     on an unrelated page)
//  2. the active tab's URL via check-duplicate — activeTab grants url
//     access when the panel came from a user action; the optional `tabs`
//     permission covers direct opens (gratable from the empty state)
//  3. fuzzy match on the tab's title (forum pages about the tool)
//The panel is persistent: it follows tab switches and navigations, and
//reacts to a new Discuss click while already open (storage.onChanged).
export default function ExtensionChat() {
    const [target, setTarget] = useState(null)
    const [loading, setLoading] = useState(true)
    const [canGrant, setCanGrant] = useState(false)
    const seqRef = useRef(0)
    const resolveTabRef = useRef(null)
    const retargetRef = useRef(null)
    //the page that produced the DISPLAYED target — retarget pins are
    //stamped with it, never with whatever tab a newer resolve happens to
    //be looking at
    const displayRef = useRef(null)

    useEffect(() => {
        let alive = true
        //events must be scoped to the window this panel lives in — a second
        //browser window's tabs must never change this panel's thread
        let myWindowId = null
        browser.windows.getCurrent().then(w => { myWindowId = w?.id }).catch(() => {})

        const apply = (t, tab) => {
            if (!alive) return
            displayRef.current = t ? { tabId: tab?.id ?? null, url: tab?.url ?? null } : null
            setTarget(t || null)
            setLoading(false)
        }

        //a stored url MUST verify — when temporary page access is revoked
        //(cross-origin nav) tab.url comes back empty and must not match,
        //otherwise an unrelated page inherits a previous tab's pick
        const samePage = (x, tab) =>
            x && (x.tabId == null || x.tabId === tab?.id) &&
            (!x.url || x.url === tab?.url)

        const resolveTab = async (tab, mine) => {
            try {
                //1. explicit Discuss target, scoped to its tab+url — unless
                //the user pinned a fuzzy alternative afterwards (newer `at`
                //wins; a re-clicked Discuss restamps and takes it back).
                //Pins live per tab so a pick on one tab never displaces
                //another tab's choice
                const { dealChatTarget: stored, dealChatPins: pins } =
                    await browser.storage.session.get(['dealChatTarget', 'dealChatPins'])
                const pin = tab?.id != null ? pins?.[tab.id] : null
                const pagePinned = pin && pin.target?.toolSlug && (!pin.url || pin.url === tab?.url) ? pin : null
                if (samePage(stored, tab) && stored.toolSlug) {
                    const pick = pagePinned && (pagePinned.at || 0) > (stored.at || 0) ? pagePinned.target : stored
                    if (mine === seqRef.current) apply(pick, tab)
                    return
                }
                //a pin alone also resolves — e.g. the stored target was
                //stamped for another tab
                if (pagePinned) {
                    if (mine === seqRef.current) apply(pagePinned.target, tab)
                    return
                }
                //2. URL match; 3. fuzzy by title — only when the tab is
                //readable (tabs perm or activeTab grant on that tab)
                if (tab?.url?.startsWith('http')) {
                    for (const fn of [() => resolveChatTarget(tab.url), () => resolveChatTargetFuzzy(tab.title, tab.url)]) {
                        const r = await fn().catch(() => null)
                        if (mine === seqRef.current && r) { apply(r, tab); return }
                    }
                }
                if (mine === seqRef.current) apply(null)
            } catch(e) {
                if (mine === seqRef.current) apply(null)
            }

            //tab metadata unreadable → surface the opt-in grant instead
            //of silently landing on the empty state
            if (alive && mine === seqRef.current && !tab?.url && browser.permissions?.request) {
                try {
                    setCanGrant(!(await browser.permissions.contains({ permissions: ['tabs'] })))
                } catch(e) {}
            }
        }

        //the seq token wraps the whole lookup+resolve: a stale tab query
        //(success OR failure) can't overwrite a newer resolution
        const run = async (lookupTab) => {
            const mine = ++seqRef.current
            try {
                const tab = await lookupTab()
                if (mine === seqRef.current) await resolveTab(tab, mine)
            } catch(e) {
                if (mine === seqRef.current) apply(null)
            }
        }
        resolveTabRef.current = (tab) => run(() => Promise.resolve(tab))

        //user picked an alternative: kill any pending resolve so a stale
        //lookup can't re-apply the previous guess, pin the pick to the
        //page that produced the displayed target, then re-resolve — on
        //the same page the pin wins, on a new tab the page resolves fresh
        retargetRef.current = async (t) => {
            ++seqRef.current
            setTarget(t)
            const page = displayRef.current
            //a pin without a url can't be scoped to the page — it would
            //match every later page in the tab, so skip persisting
            if (page?.tabId != null && page.url) {
                try {
                    const { dealChatPins: pins } = await browser.storage.session.get('dealChatPins')
                    await browser.storage.session.set({
                        dealChatPins: { ...pins, [page.tabId]: { target: t, url: page.url, at: Date.now() } }
                    })
                } catch(e) {}
            }
            refresh()
        }

        const refresh = () =>
            run(() => browser.tabs.query({ active: true, currentWindow: true }).then(([t]) => t))

        refresh()

        //persistent surface: follow tab activation/navigation; where the
        //tab's url isn't readable we show the empty state rather than a
        //stale thread from the previous tab
        const onActivated = ({ tabId, windowId }) => {
            //filter only once the window is known — before that, the initial
            //refresh() already covered the current tab
            if (myWindowId != null && windowId !== myWindowId) return
            run(() => browser.tabs.get(tabId).then(t => t?.active ? t : null))
        }
        const onUpdated = (tabId, change, tab) => {
            if (myWindowId != null && tab?.windowId !== myWindowId) return
            if (tab?.active && (change.url || change.status === 'loading'))
                run(() => Promise.resolve(tab))
        }
        //a Discuss click while the panel is already open re-stamps the
        //target — pick it up without requiring a route reload
        const onStorage = (changes, area) => {
            if (area === 'session' && changes.dealChatTarget) refresh()
        }

        browser.tabs.onActivated.addListener(onActivated)
        browser.tabs.onUpdated.addListener(onUpdated)
        browser.storage.onChanged.addListener(onStorage)
        return () => {
            alive = false
            browser.tabs.onActivated.removeListener(onActivated)
            browser.tabs.onUpdated.removeListener(onUpdated)
            browser.storage.onChanged.removeListener(onStorage)
        }
    }, [])

    return (
        <Screen>
            {loading && (
                <div style={{padding: '1rem', color: 'var(--secondary-text-color)'}}>…</div>
            )}

            {!loading && !target && (
                <div style={{padding: '1rem', display:'flex', flexDirection:'column', gap:'0.4rem', alignItems:'center', color: 'var(--secondary-text-color)'}}>
                    <Icon name='micro_comment' />
                    <div>{t.s('openADealPage')}</div>
                    {canGrant && (
                        <Button variant='outline' onClick={async () => {
                            try {
                                //user gesture required — this is a real click
                                if (await browser.permissions.request({ permissions: ['tabs'] })) {
                                    setCanGrant(false)
                                    const [tab] = await browser.tabs.query({ active: true, currentWindow: true })
                                    resolveTabRef.current?.(tab)
                                }
                            } catch(e) {}
                        }}>
                            {t.s('detectToolOnPage')}
                        </Button>
                    )}
                </div>
            )}

            {!loading && target && (
                <DealChat target={target} fill onRetarget={(t) => retargetRef.current?.(t)} />
            )}
        </Screen>
    )
}
